import React, { useState } from 'react';
import { ChevronLeft, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Calendar } from '../components/Calendar';
import { TimePicker } from '../components/TimePicker';
import { DepositPolicyModal } from '../components/DepositPolicyModal';
import { BookingRequest } from '../App';
interface EditBookingProps {
  bookingId: string;
  currentBooking: BookingRequest;
  onBack: () => void;
  onSave: (booking: BookingRequest) => void;
  onCancel: () => void;
}
export function EditBooking({
  currentBooking,
  onBack,
  onSave,
  onCancel
}: EditBookingProps) {
  const [selectedDates, setSelectedDates] = useState<Date[]>([currentBooking.startDate, currentBooking.endDate]);
  const [pickupTime, setPickupTime] = useState(currentBooking.pickupTime);
  const [dropoffTime, setDropoffTime] = useState(currentBooking.dropoffTime);
  const [ownerNote, setOwnerNote] = useState(currentBooking.ownerNote);
  const [showPickupTimePicker, setShowPickupTimePicker] = useState(false);
  const [showDropoffTimePicker, setShowDropoffTimePicker] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const mockItem = {
    title: currentBooking.itemTitle,
    image: currentBooking.itemImage,
    pricing: {
      day: 15
    },
    deposit: currentBooking.pricing.deposit
  };
  const calculateTotal = () => {
    const days = selectedDates.length === 2 ? Math.max(1, Math.ceil((selectedDates[1].getTime() - selectedDates[0].getTime()) / (1000 * 60 * 60 * 24))) : 1;
    const rentalAmount = mockItem.pricing.day * days;
    return {
      rental: rentalAmount,
      deposit: mockItem.deposit,
      total: rentalAmount,
      originalTotal: currentBooking.pricing.total,
      difference: rentalAmount - currentBooking.pricing.total
    };
  };
  const totals = calculateTotal();
  const handleSave = () => {
    const updatedBooking: BookingRequest = {
      ...currentBooking,
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      pickupTime,
      dropoffTime,
      ownerNote,
      pricing: {
        rental: totals.rental,
        deposit: totals.deposit,
        total: totals.total
      }
    };
    onSave(updatedBooking);
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1063">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand/50" data-id="element-1064">
        <div className="max-w-md mx-auto px-4 py-3" data-id="element-1065">
          <div className="flex items-center justify-between mb-3" data-id="element-1066">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1067">
              <ChevronLeft size={24} className="text-charcoal" data-id="element-1068" />
            </button>
            <h1 className="font-semibold text-charcoal" data-id="element-1069">Edit Booking</h1>
            <div className="w-10" data-id="element-1070" />
          </div>

          {/* Progress Indicator */}
          <div className="flex gap-2" data-id="element-1071">
            <div className="h-1 flex-1 rounded-full bg-dark-green" data-id="element-1072" />
            <div className="h-1 flex-1 rounded-full bg-sand" data-id="element-1073" />
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-5" data-id="element-1074">
        {/* Cancel Booking Button */}
        <Button variant="secondary" fullWidth onClick={() => setShowCancelConfirm(true)} className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100" data-id="element-1075">
          Cancel Booking
        </Button>

        {/* Item Summary Card */}
        <div className="flex gap-3 bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50" data-id="element-1076">
          <img src={mockItem.image} alt={mockItem.title} className="w-16 h-16 rounded-ios object-cover" data-id="element-1077" />
          <div className="flex-1" data-id="element-1078">
            <h3 className="font-semibold text-charcoal" data-id="element-1079">{mockItem.title}</h3>
            <p className="text-sm text-moss-gray mt-1" data-id="element-1080">
              Select your rental period
            </p>
            <div className="mt-2" data-id="element-1081">
              <span className="text-lg font-bold text-dark-green" data-id="element-1082">
                ${mockItem.pricing.day}
              </span>
              <span className="text-xs text-moss-gray ml-1" data-id="element-1083">per day</span>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div data-id="element-1084">
          <label className="block text-sm font-semibold text-charcoal mb-3" data-id="element-1085">
            Select Dates
          </label>
          <Calendar selectedDates={selectedDates} onDateSelect={setSelectedDates} mode="range" data-id="element-1086" />
        </div>

        {/* Time Selection */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1087">
          <div data-id="element-1088">
            <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-1089">
              Select Pick-up time
            </label>
            <button onClick={() => setShowPickupTimePicker(true)} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-left text-charcoal font-medium hover:border-dark-green/30 transition-colors active:scale-[0.98]" data-id="element-1090">
              {pickupTime}
            </button>
          </div>
          <div data-id="element-1091">
            <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-1092">
              Select Drop-Off time
            </label>
            <button onClick={() => setShowDropoffTimePicker(true)} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-left text-charcoal font-medium hover:border-dark-green/30 transition-colors active:scale-[0.98]" data-id="element-1093">
              {dropoffTime}
            </button>
          </div>
        </div>

        {/* Optional Note */}
        <div data-id="element-1094">
          <label className="block text-sm font-semibold text-charcoal mb-2" data-id="element-1095">
            Add note for owner (optional)
          </label>
          <textarea value={ownerNote} onChange={e => setOwnerNote(e.target.value)} placeholder="Anything else the owner should know before reviewing your request?" rows={3} className="w-full px-4 py-3 bg-white border-2 border-sand rounded-ios text-charcoal placeholder-moss-gray focus:outline-none focus:border-dark-green resize-none" data-id="element-1096" />
        </div>

        {/* Price Summary with Difference */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50 space-y-2" data-id="element-1097">
          <div className="flex justify-between text-sm" data-id="element-1098">
            <span className="text-moss-gray" data-id="element-1099">Rental amount</span>
            <span className="font-medium text-charcoal" data-id="element-1100">${totals.rental}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t border-sand/50" data-id="element-1101">
            <button onClick={() => setShowDepositModal(true)} className="flex items-center gap-1 text-moss-gray hover:text-dark-green transition-colors" data-id="element-1102">
              Deposit (Hold)
              <Info size={14} className="text-dark-green" data-id="element-1103" />
            </button>
            <span className="font-medium text-charcoal" data-id="element-1104">${totals.deposit}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-sand/50" data-id="element-1105">
            <span className="font-bold text-charcoal" data-id="element-1106">New Total</span>
            <span className="font-bold text-dark-green text-lg" data-id="element-1107">
              ${totals.total}
            </span>
          </div>
          {totals.difference !== 0 && <div className="flex justify-between pt-2 border-t border-sand/50" data-id="element-1108">
              <span className="text-sm text-moss-gray" data-id="element-1109">
                {totals.difference > 0 ? 'Additional charge' : 'Refund'}
              </span>
              <span className={`text-sm font-semibold ${totals.difference > 0 ? 'text-red-600' : 'text-green-600'}`} data-id="element-1110">
                {totals.difference > 0 ? '+' : ''}${Math.abs(totals.difference)}
              </span>
            </div>}
        </motion.div>

        <Button variant="primary" fullWidth onClick={handleSave} className="h-12" data-id="element-1111">
          Save Changes
        </Button>
      </div>

      {/* Time Pickers */}
      <TimePicker isOpen={showPickupTimePicker} onClose={() => setShowPickupTimePicker(false)} onSelect={setPickupTime} initialTime={pickupTime} label="Select Pick-up time" data-id="element-1112" />

      <TimePicker isOpen={showDropoffTimePicker} onClose={() => setShowDropoffTimePicker(false)} onSelect={setDropoffTime} initialTime={dropoffTime} label="Select Drop-Off time" data-id="element-1113" />

      {/* Deposit Policy Modal */}
      <DepositPolicyModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)} depositAmount={mockItem.deposit} data-id="element-1114" />

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setShowCancelConfirm(false)} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-1115" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6" data-id="element-1116">
            <h2 className="text-xl font-bold text-charcoal mb-2" data-id="element-1117">
              Cancel Booking?
            </h2>
            <p className="text-sm text-moss-gray mb-6" data-id="element-1118">
              This will cancel your booking request. You won't be charged.
            </p>
            <div className="flex gap-3" data-id="element-1119">
              <Button variant="secondary" onClick={() => setShowCancelConfirm(false)} className="flex-1" data-id="element-1120">
                Keep Booking
              </Button>
              <Button variant="primary" onClick={onCancel} className="flex-1 bg-red-600 hover:bg-red-700" data-id="element-1121">
                Cancel Booking
              </Button>
            </div>
          </motion.div>
        </>}
    </div>;
}